import {
  DataGrid,
  type DataGridCellRendererProps,
  type DataGridColumnSettings,
  type DataGridSettings,
  OverlayScrollDetectionType,
} from '@myosh/odin-components';
import { format, parseISO } from 'date-fns';
import { type Key, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { tableDataApi, useCrossTabReportTableDataQuery } from '../../redux/config/table-data';
import type { CrossTabDataColumnProps } from '../../redux/config/table-data.interfaces';
import type { WidgetConfigurationData } from '../../redux/config/widgetApi';
import { useAppDispatch } from '../../redux/hooks';
import { getWidgets } from '../../redux/slices/layout-tabs-slice';
import CustomClickableCellComponent from '../CustomClickableCellComponent/custom-clickable-cell.component';
import OdinCustomDetailsWindow from '../CustomDetailsWindow/custom-details-window.component';
import ChartGenericWrapper from '../common/chart-generic-wrapper';
import type { ChartProps } from './chart.interface';
import { genericSort, SortDirection } from './generalFunctions/general-functions';
import useStartEndDates from './hooks/useStartEndDates';

export interface CrossTabReportDataProps {
  firstField: string;
  [field: string]: Key;
}

export default function CrossTabReport({ widgetId, layoutProps }: ChartProps) {
  const [crossTabReportTableData, setCrossTabReportTableData] = useState<Array<CrossTabReportDataProps>>();
  const [columns, setColumns] = useState<Array<DataGridColumnSettings>>();
  const [configurationData, setConfigurationData] = useState<WidgetConfigurationData>();
  const [detailsWindowVisible, setDetailsWindowVisible] = useState<boolean>(false);
  const [cellDetail, setCellDetail] = useState<{ rowName: string; columnId: string }>();

  const dispatch = useAppDispatch();
  const { startDate, endDate } = useStartEndDates();
  const widgets = useSelector(getWidgets);

  const {
    data: widgetChartData,
    isLoading,
    isFetching: isWidgetChartDataFetching,
    isError,
    isUninitialized: isDataUninitialized,
    error,
  } = useCrossTabReportTableDataQuery({ widgetId, from: startDate, to: endDate });

  const showCellDetails = async (rowName: string, columnId: string) => {
    setCellDetail({ rowName, columnId });
    setDetailsWindowVisible(true);
  };

  useEffect(() => {
    if (widgets) {
      setConfigurationData(widgets.find((widget) => +widget.widget.id === +widgetId)?.widget);
    }
  }, [widgets, widgetId]);

  useEffect(() => {
    if (widgetChartData && !isLoading && !isWidgetChartDataFetching) {
      const firstKey = Object.keys(widgetChartData);

      if (firstKey.length > 0) {
        const gridColumns: Array<DataGridColumnSettings> = widgetChartData[firstKey[0]].map((column) => {
          return {
            id: column.secondField,
            title: column.secondField,
            field: column.secondField,
            visible: true,
            cellRenderer: 'CustomClickableCellComponent',
          };
        });

        setColumns([
          {
            id: 'firstField',
            title: '',
            field: 'firstField',
            visible: true,
            isIdField: true,
          },
          ...gridColumns,
        ]);
      }
    }
  }, [widgetChartData, isLoading, isWidgetChartDataFetching]);

  useEffect(() => {
    if (widgetChartData && !isLoading && !isWidgetChartDataFetching) {
      const dataGridRows: Array<CrossTabReportDataProps> = [];
      for (const key in widgetChartData) {
        const gridRowData = widgetChartData[key].reduce(
          (
            previousValue: CrossTabReportDataProps | CrossTabReportDataProps[],
            currentValue: CrossTabDataColumnProps
          ) => {
            previousValue = {
              ...previousValue,
              firstField: key,
              [currentValue.secondField]: currentValue.value,
            };
            return previousValue;
          },
          []
        );

        dataGridRows.push(gridRowData as CrossTabReportDataProps);
      }
      setCrossTabReportTableData(genericSort(dataGridRows, 'firstField', SortDirection.Ascending));
    }
  }, [widgetChartData, isLoading, isWidgetChartDataFetching]);

  const gridSettings = useMemo<DataGridSettings | undefined>(() => {
    if (columns && columns.length > 0) {
      return {
        columns,
        scrollDetection: OverlayScrollDetectionType.All,
        fullHeight: true,
        components: {
          CustomClickableCellComponent: {
            CellComponent: (props: DataGridCellRendererProps) => {
              return <CustomClickableCellComponent dataGrid={props} handleCellClick={showCellDetails} />;
            },
          },
        },
      };
    }
  }, [columns]);

  const handleRefetch = () => {
    if (!isDataUninitialized && crossTabReportTableData && !isWidgetChartDataFetching) {
      dispatch(tableDataApi.util.invalidateTags([{ type: 'CrossTabReportData', id: `LIST-${widgetId}` } as const]));
    }
  };

  const onDialogHidden = () => setDetailsWindowVisible(false);

  return (
    <ChartGenericWrapper
      widgetId={widgetId}
      isLoading={isLoading}
      isFetching={isWidgetChartDataFetching}
      refetch={handleRefetch}
      isError={isError}
      error={error}
      widgetConfigurationData={configurationData && { ...configurationData, layoutProps }}
      widgetChartData={widgetChartData}
      renderNoDataByDefault={!isWidgetChartDataFetching && !crossTabReportTableData?.length}
    >
      {configurationData && (
        <OdinCustomDetailsWindow
          {...configurationData?.widgetData}
          widgetId={widgetId}
          fieldValue={cellDetail?.rowName}
          secondFieldValue={
            cellDetail?.columnId && parseISO(cellDetail?.columnId).toString() !== 'Invalid Date'
              ? format(parseISO(cellDetail?.columnId), 'dd/MM/yyyy')
              : ''
          }
          visible={detailsWindowVisible}
          onDialogHidden={onDialogHidden}
          moduleViewId={configurationData?.widgetData?.moduleViewId ?? ''}
        />
      )}
      {gridSettings && crossTabReportTableData && (
        <DataGrid data={crossTabReportTableData} gridSettings={gridSettings} showSettings={false} />
      )}
    </ChartGenericWrapper>
  );
}
